import { RegisterMediaPayload } from '@brands/services/communication/registersConversationMedia';
import { InsuranceForm, defaultInsuranceForm } from '../../../Insurance/utils/types';
import { Checkbox, Typography } from '@mui/material';
import PhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import { E164Number } from 'libphonenumber-js/types';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { isEqual } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoChevronDown, IoChevronUp, IoClose, IoDocumentOutline } from 'react-icons/io5';

import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import { confirmOneReportMedia } from '../../../../services/oneReport/confirmOneReportMedia';
import { getOneReportMedia } from '../../../../services/oneReport/getOneReportMedia';
import { registerOneReportMedia } from '../../../../services/oneReport/registerOneReportMedia';
import { createPatientInsurance } from '../../../../services/identity/createPatientInsurance';
import { listInsuranceCarriers, InsuranceCarrier } from '../../../../services/identity/listInsuranceCarriers';
import { removePatientInsurance } from '../../../../services/identity/removePatientInsurance';
import { Insurance, UserProfile, UserRoleName } from '../../../../services/identity/types/UserProfile';
import { base64ListToFileList } from '../../../../Utils/base64ToFile';
import { displayErrorDetails } from '../../../../Utils/displayError';
import { getFileMimeType } from '../../../../Utils/truncateString';
import { isValidZipCode } from '../../../../Utils/validation';
import { genderOptions } from '../../../../Utils/selectButtonOptions';
import { insuranceThroughOptionsForInsurancePage, stateOptions } from '../../../../Utils/selectOptions';
import {
  UploadedFile,
  updateDataInsuranceNewDesign,
  updateDataSecondaryInsuranceNewDesign,
} from '../../../MyProfile/Patient/utils/utilsFunctions';
import { toast } from 'react-toastify';
import { truncateFilename } from '@brands/Utils/truncateString';

import RemoveItemModal from '../../Components/RemoveItemModal/RemoveItemModal';
import styles from './collectorEditUser.module.scss';

const stateSelectOptions = stateOptions.map((opt) => ({
  value: opt.value,
  label: opt.label,
  id: opt.id,
}));

const relationshipOptions = insuranceThroughOptionsForInsurancePage.map((o) => ({
  id: o.value,
  value: o.value,
  label: o.label,
}));

/** Always show in UI: Myself + Spouse, Parent, … */
const policyHolderOptions = [
  { id: 'myself', value: 'myself', label: 'Myself' },
  ...relationshipOptions,
];

const genderSelectOptions = genderOptions.map((g) => ({
  id: g.id,
  value: g.value,
  label: g.label,
}));

const STATE_FUNDED_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'not_sure', label: 'Not sure' },
];
export interface InsuranceTierFormState {
  carrierId: number | '';
  memberId: string;
  groupNumber: string;
  phone: string;
  address1: string;
  city: string;
  state: string;
  zip: string;
  frontFiles: UploadedFile[];
  backFiles: UploadedFile[];
  retainedFrontMediaIds: string[];
  retainedBackMediaIds: string[];
  retainedFrontFiles: RetainedUploadedFile[];
  retainedBackFiles: RetainedUploadedFile[];
  /** Who holds the policy: myself | spouse | parent | … */
  insuredByRelation: string;
  subFirstName: string;
  subLastName: string;
  subDob: string;
  subGender: string;
  subAddress1: string;
  subAddress2: string;
  subCity: string;
  subState: string;
  subZip: string;
  subscriberSameAddress: boolean;
  stateFunded: string;
}

type InsuranceMedia = {
  media_id: string;
  type: string;
};

type RetainedUploadedFile = UploadedFile & { mediaId: string };

function normalizeStateFunded(v?: string | null): string {
  if (!v) return 'no';
  const x = String(v).toLowerCase();
  if (x === 'yes') return 'yes';
  if (x === 'no') return 'no';
  if (x === 'not_sure' || x.includes('not sure')) return 'not_sure';
  return 'no';
}

function emptyTier(): InsuranceTierFormState {
  return {
    carrierId: '',
    memberId: '',
    groupNumber: '',
    phone: '',
    address1: '',
    city: '',
    state: '',
    zip: '',
    frontFiles: [],
    backFiles: [],
    retainedFrontMediaIds: [],
    retainedBackMediaIds: [],
    retainedFrontFiles: [],
    retainedBackFiles: [],
    insuredByRelation: 'myself',
    subFirstName: '',
    subLastName: '',
    subDob: '',
    subGender: '',
    subAddress1: '',
    subAddress2: '',
    subCity: '',
    subState: '',
    subZip: '',
    subscriberSameAddress: false,
    stateFunded: 'no',
  };
}

function normalizeInsuredByForForm(insuredBy?: string | null): string {
  if (!insuredBy || insuredBy === 'none' || insuredBy === '') return 'myself';
  const lower = insuredBy.toLowerCase();
  if (lower === 'myself' || lower === 'self') return 'myself';
  return insuredBy;
}

function insuranceToTier(ins: Insurance | undefined): InsuranceTierFormState {
  if (!ins) return emptyTier();
  const m = ins.insurance_membership;
  const t = ins.insurance_through;
  const addr = m?.address;
  const insuredByRelation = normalizeInsuredByForForm(ins.insured_by);
  const mediaFrontIds = (ins.media || []).filter((x) => x.type === 'front_of_card').map((x) => x.media_id);
  const mediaBackIds = (ins.media || []).filter((x) => x.type === 'back_of_card').map((x) => x.media_id);
  const explicitFrontIds = ins.front_of_card?.map((x) => x.media_id) || [];
  const explicitBackIds = ins.back_of_card?.map((x) => x.media_id) || [];
  const retainedFrontMediaIds = [...new Set([...mediaFrontIds, ...explicitFrontIds])];
  const retainedBackMediaIds = [...new Set([...mediaBackIds, ...explicitBackIds])];

  return {
    carrierId: m?.insurance_carrier_id ?? m?.insurance_carrier?.id ?? '',
    memberId: m?.member_id || '',
    groupNumber: m?.group_number || '',
    phone: m?.phone_number || '',
    address1: m?.address1 || addr?.address1 || '',
    city: m?.city || addr?.city || '',
    state: m?.state || addr?.state || '',
    zip: m?.zip_code || addr?.zip_code || '',
    frontFiles: [],
    backFiles: [],
    retainedFrontMediaIds,
    retainedBackMediaIds,
    retainedFrontFiles: [],
    retainedBackFiles: [],
    insuredByRelation,
    subFirstName: t?.first_name || '',
    subLastName: t?.last_name || '',
    subDob: t?.date_of_birth ? moment(t.date_of_birth).format('YYYY-MM-DD') : '',
    subGender: t?.gender_identity || '',
    subAddress1: t?.address?.address1 || '',
    subAddress2: t?.address?.address2 || '',
    subCity: t?.address?.city || '',
    subState: t?.address?.state || '',
    subZip: t?.address?.zip_code || '',
    subscriberSameAddress: false,
    stateFunded: normalizeStateFunded(ins.state_funded_coverage_status),
  };
}

function applySubscriberAddress(
  tier: InsuranceTierFormState,
  user: UserProfile | null,
): {
  addr1: string;
  addr2: string;
  city: string;
  state: string;
  zip: string;
} {
  if (tier.subscriberSameAddress && user?.address) {
    return {
      addr1: user.address.address1 || '',
      addr2: user.address.address2 || '',
      city: user.address.city || '',
      state: user.address.state || '',
      zip: user.address.zip_code || '',
    };
  }
  return {
    addr1: tier.subAddress1,
    addr2: tier.subAddress2,
    city: tier.subCity,
    state: tier.subState,
    zip: tier.subZip,
  };
}

function tierToMergedPrimaryPartial(tier: InsuranceTierFormState, user: UserProfile | null): Partial<InsuranceForm> {
  const sub = applySubscriberAddress(tier, user);
  const insurance_through = tier.insuredByRelation;
  return {
    member_id: tier.memberId,
    group_number: tier.groupNumber,
    insurance_through,
    insurance_carrier_id: tier.carrierId === '' ? null : tier.carrierId,
    insurance_phone_number: tier.phone,
    insurance_address: tier.address1,
    insurance_city: tier.city,
    insurance_state: tier.state,
    insurance_zip_code: tier.zip,
    insurance_first_name: tier.subFirstName,
    insurance_last_name: tier.subLastName,
    insurance_date_of_birth: tier.subDob,
    insurance_gender: tier.subGender,
    insurance_address1: sub.addr1,
    insurance_address2: sub.addr2,
    insurance_city1: sub.city,
    insurance_state1: sub.state,
    insurance_zip_code1: sub.zip,
    front_of_card: tier.frontFiles,
    back_of_card: tier.backFiles,
    state_funded_coverage_status: tier.stateFunded,
  };
}

function tierToMergedSecondaryPartial(tier: InsuranceTierFormState, user: UserProfile | null): Partial<InsuranceForm> {
  const sub = applySubscriberAddress(tier, user);
  const insurance_through = tier.insuredByRelation;
  return {
    secondary_member_id: tier.memberId,
    secondary_group_number: tier.groupNumber,
    secondary_insurance_through: insurance_through,
    secondary_insurance_carrier_id: tier.carrierId === '' ? null : tier.carrierId,
    secondary_insurance_phone_number: tier.phone,
    secondary_insurance_address: tier.address1,
    secondary_insurance_city: tier.city,
    secondary_insurance_state: tier.state,
    secondary_insurance_zip_code: tier.zip,
    secondary_insurance_first_name: tier.subFirstName,
    secondary_insurance_last_name: tier.subLastName,
    secondary_insurance_date_of_birth: tier.subDob,
    secondary_insurance_gender: tier.subGender,
    secondary_insurance_address1: sub.addr1,
    secondary_insurance_address2: sub.addr2,
    secondary_insurance_city1: sub.city,
    secondary_insurance_state1: sub.state,
    secondary_insurance_zip_code1: sub.zip,
    secondary_front_of_card: tier.frontFiles,
    secondary_back_of_card: tier.backFiles,
  };
}

const mapMediaToInsuranceMedia = (media: string[], type: string): InsuranceMedia[] =>
  media.map((mediaId) => ({ media_id: mediaId, type }));

async function uploadInsuranceCardFiles(imagesValue: UploadedFile[], userRole: string): Promise<string[]> {
  const newUploadedImages = imagesValue.filter((image) => image.dataURL.startsWith('data:'));
  const existingImages = imagesValue.filter((image) => image.dataURL.startsWith('https://'));

  const imageList = newUploadedImages.map((image) => image.dataURL);
  const fileList = await base64ListToFileList(imageList || []);
  const mediaIDs: string[] = [];

  if (Array.isArray(newUploadedImages) && Array.isArray(fileList)) {
    await Promise.all(
      Array.from(fileList).map(async (file, index) => {
        const image = newUploadedImages[index] as UploadedFile;
        const mimeType = getFileMimeType(image.name) || file.type || 'application/octet-stream';
        const payload: RegisterMediaPayload = {
          files: [{ filename: image.name, mimetype: mimeType }],
        };
        try {
          const registeredMedia = await registerOneReportMedia(payload, userRole);
          const mediaId = registeredMedia.files[0].upload_id;
          await fetch(registeredMedia.files[0].upload_url, {
            method: 'PUT',
            body: file,
            headers: { 'content-type': mimeType },
          });
          await confirmOneReportMedia(mediaId, userRole);
          mediaIDs.push(mediaId);
        } catch (error: unknown) {
          displayErrorDetails(error);
        }
      }),
    );
  }

  if (existingImages.length > 0) {
    const fromExisting = existingImages.map((image) => (image as UploadedFile & { media_id?: string }).media_id);
    mediaIDs.push(...(fromExisting.filter(Boolean) as string[]));
  }
  return mediaIDs;
}

function readFileAsUploaded(file: File): Promise<UploadedFile> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve({ name: file.name, dataURL: reader.result as string });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function isImageFileDataUrl(dataURL: string, filename?: string): boolean {
  if (/^data:image/i.test(dataURL)) return true;
  if (/^https?:\/\//i.test(dataURL)) {
    const ext = (filename ?? '').split('.').pop()?.toLowerCase() ?? '';
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
  }
  return false;
}

async function loadRetainedMediaFiles(mediaIds: string[], userRole: string): Promise<RetainedUploadedFile[]> {
  if (mediaIds.length === 0) return [];
  const pairs = await Promise.all(
    mediaIds.map(async (mediaId) => {
      try {
        const m = await getOneReportMedia(mediaId, userRole);
        if (!m?.url) return null;
        return { name: m.filename || 'Insurance card', dataURL: m.url, mediaId };
      } catch {
        return null;
      }
    }),
  );
  return pairs.filter((p): p is NonNullable<typeof p> => p !== null);
}

interface CollectorInsuranceTabProps {
  user: UserProfile | null;
  onUserSaved?: () => void | Promise<void>;
  userRole?: string;
  formRef?: React.RefObject<HTMLFormElement>;
  setIsDirty?: (dirty: boolean) => void;
}

const InsuranceTierPanel: React.FC<{
  title: string;
  headerCarrierName: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, headerCarrierName, expanded, onToggle, children }) => (
  <div className={styles.insuranceSection}>
    <button type="button" className={styles.insuranceHeader} onClick={onToggle}>
      <div className={styles.insuranceHeaderLeft}>
        {expanded ? <IoChevronUp /> : <IoChevronDown />}
        <span className={styles.insuranceHeaderTitle}>{title}</span>
      </div>
      <span className={styles.insuranceHeaderCarrier}>{headerCarrierName}</span>
    </button>
    {expanded && <div className={styles.insuranceBody}>{children}</div>}
  </div>
);

const CollectorInsuranceTab: React.FC<CollectorInsuranceTabProps> = ({
  user,
  onUserSaved,
  userRole = UserRoleName.Collector,
  formRef,
  setIsDirty,
}) => {
  const [carriers, setCarriers] = useState<InsuranceCarrier[]>([]);
  const [primaryExpanded, setPrimaryExpanded] = useState(true);
  const [secondaryExpanded, setSecondaryExpanded] = useState(false);
  const [primary, setPrimary] = useState<InsuranceTierFormState>(() => emptyTier());
  const [secondary, setSecondary] = useState<InsuranceTierFormState>(() => emptyTier());
  const [phoneErrors, setPhoneErrors] = useState<{ primary?: string; secondary?: string }>({});
  const [zipErrors, setZipErrors] = useState<{
    primaryZip?: string;
    primarySubZip?: string;
    secondaryZip?: string;
    secondarySubZip?: string;
  }>({});
  const [saving, setSaving] = useState(false);
  const [removeInsuranceModal, setRemoveInsuranceModal] = useState(false);

  /** Snapshot of last loaded/saved tiers for dirty detection. */
  const savedTiersRef = useRef<{ primary: InsuranceTierFormState; secondary: InsuranceTierFormState } | null>(null);

  useEffect(() => {
    listInsuranceCarriers()
      .then(setCarriers)
      .catch((e) => displayErrorDetails(e));
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const insurances = user.patient_profile?.insurances || [];
    const prim = insurances.find((i) => !i.is_secondary && i.is_current);
    const sec = insurances.find((i) => i.is_secondary && i.is_current);
    const primaryTier = insuranceToTier(prim);
    const secondaryTier = insuranceToTier(sec);

    const hydrateInsuranceMedia = async (): Promise<void> => {
      const [primaryFront, primaryBack, secondaryFront, secondaryBack] = await Promise.all([
        loadRetainedMediaFiles(primaryTier.retainedFrontMediaIds, userRole),
        loadRetainedMediaFiles(primaryTier.retainedBackMediaIds, userRole),
        loadRetainedMediaFiles(secondaryTier.retainedFrontMediaIds, userRole),
        loadRetainedMediaFiles(secondaryTier.retainedBackMediaIds, userRole),
      ]);
      if (cancelled) return;
      const hydratedPrimary = { ...primaryTier, retainedFrontFiles: primaryFront, retainedBackFiles: primaryBack };
      const hydratedSecondary = {
        ...secondaryTier,
        retainedFrontFiles: secondaryFront,
        retainedBackFiles: secondaryBack,
      };
      setPrimary(hydratedPrimary);
      setSecondary(hydratedSecondary);
      savedTiersRef.current = { primary: hydratedPrimary, secondary: hydratedSecondary };
      setPhoneErrors({});
      setZipErrors({});
    };

    void hydrateInsuranceMedia();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!savedTiersRef.current) return;
    const changed =
      !isEqual(primary, savedTiersRef.current.primary) || !isEqual(secondary, savedTiersRef.current.secondary);
    setIsDirty?.(changed);
  }, [primary, secondary, setIsDirty]);

  const carrierOptions = useMemo(
    () =>
      carriers.map((c) => ({
        id: c.id,
        value: String(c.id),
        label: c.name,
      })),
    [carriers],
  );

  const primaryCarrierName = useMemo(() => {
    if (primary.carrierId === '') return '';
    return carriers.find((c) => c.id === primary.carrierId)?.name || '';
  }, [carriers, primary.carrierId]);

  const secondaryCarrierName = useMemo(() => {
    if (secondary.carrierId === '') return '';
    return carriers.find((c) => c.id === secondary.carrierId)?.name || '';
  }, [carriers, secondary.carrierId]);

  const updatePrimary = useCallback((patch: Partial<InsuranceTierFormState>) => {
    if (patch.phone !== undefined) {
      setPhoneErrors((prev) => ({ ...prev, primary: undefined }));
    }
    if (patch.zip !== undefined) {
      setZipErrors((prev) => ({ ...prev, primaryZip: undefined }));
    }
    if (patch.subZip !== undefined) {
      setZipErrors((prev) => ({ ...prev, primarySubZip: undefined }));
    }
    setPrimary((p) => ({ ...p, ...patch }));
  }, []);

  const updateSecondary = useCallback((patch: Partial<InsuranceTierFormState>) => {
    if (patch.phone !== undefined) {
      setPhoneErrors((prev) => ({ ...prev, secondary: undefined }));
    }
    if (patch.zip !== undefined) {
      setZipErrors((prev) => ({ ...prev, secondaryZip: undefined }));
    }
    if (patch.subZip !== undefined) {
      setZipErrors((prev) => ({ ...prev, secondarySubZip: undefined }));
    }
    setSecondary((p) => ({ ...p, ...patch }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!user?.id) return;
    if (primary.carrierId === '') {
      toast.error('Select an insurance company for primary coverage.', { className: 'fs-unmask' });
      return;
    }
    if (!primary.memberId.trim()) {
      toast.error('Member ID is required.', { className: 'fs-unmask' });
      return;
    }
    const parsedPhone = parsePhoneNumberFromString(primary.phone, 'US');
    if (!parsedPhone?.isValid()) {
      setPhoneErrors((prev) => ({ ...prev, primary: 'Please enter a valid phone number.' }));
      toast.error('Enter a valid primary insurance phone number.', { className: 'fs-unmask' });
      return;
    }
    if (primary.zip.trim() && !isValidZipCode(primary.zip.trim())) {
      setZipErrors((prev) => ({ ...prev, primaryZip: 'Please enter a valid ZIP code.' }));
      toast.error('Enter a valid primary insurance zip code.', { className: 'fs-unmask' });
      return;
    }
    if (primary.insuredByRelation !== 'myself' && !primary.subscriberSameAddress && primary.subZip.trim() && !isValidZipCode(primary.subZip.trim())) {
      setZipErrors((prev) => ({ ...prev, primarySubZip: 'Please enter a valid ZIP code.' }));
      toast.error('Enter a valid primary policy holder zip code.', { className: 'fs-unmask' });
      return;
    }

    setSaving(true);
    try {
      const merged: InsuranceForm = {
        ...defaultInsuranceForm,
        ...tierToMergedPrimaryPartial(primary, user),
        state_funded_coverage_status: primary.stateFunded,
      };

      if (secondary.carrierId !== '') {
        Object.assign(merged, tierToMergedSecondaryPartial(secondary, user));
        merged.state_funded_coverage_status = primary.stateFunded;
      }

      let frontIds = await uploadInsuranceCardFiles(primary.frontFiles, userRole);
      let backIds = await uploadInsuranceCardFiles(primary.backFiles, userRole);
      frontIds = [...primary.retainedFrontMediaIds, ...frontIds];
      backIds = [...primary.retainedBackMediaIds, ...backIds];

      const primaryIns = updateDataInsuranceNewDesign(merged) as Insurance;
      primaryIns.front_of_card = mapMediaToInsuranceMedia(frontIds, 'front_of_card');
      primaryIns.back_of_card = mapMediaToInsuranceMedia(backIds, 'back_of_card');

      const payload: Insurance[] = [primaryIns];

      if (secondary.carrierId !== '') {
        if (!secondary.memberId.trim()) {
          toast.error('Member ID is required for secondary insurance.', { className: 'fs-unmask' });
          setSaving(false);
          return;
        }
        const sPhone = parsePhoneNumberFromString(secondary.phone, 'US');
        if (!sPhone?.isValid()) {
          setPhoneErrors((prev) => ({ ...prev, secondary: 'Please enter a valid phone number.' }));
          toast.error('Enter a valid secondary insurance phone number.', { className: 'fs-unmask' });
          setSaving(false);
          return;
        }
        if (secondary.zip.trim() && !isValidZipCode(secondary.zip.trim())) {
          setZipErrors((prev) => ({ ...prev, secondaryZip: 'Please enter a valid ZIP code.' }));
          toast.error('Enter a valid secondary insurance zip code.', { className: 'fs-unmask' });
          setSaving(false);
          return;
        }
        if (
          secondary.insuredByRelation !== 'myself' &&
          !secondary.subscriberSameAddress &&
          secondary.subZip.trim() &&
          !isValidZipCode(secondary.subZip.trim())
        ) {
          setZipErrors((prev) => ({ ...prev, secondarySubZip: 'Please enter a valid ZIP code.' }));
          toast.error('Enter a valid secondary policy holder zip code.', { className: 'fs-unmask' });
          setSaving(false);
          return;
        }

        let sFront = await uploadInsuranceCardFiles(secondary.frontFiles, userRole);
        let sBack = await uploadInsuranceCardFiles(secondary.backFiles, userRole);
        sFront = [...secondary.retainedFrontMediaIds, ...sFront];
        sBack = [...secondary.retainedBackMediaIds, ...sBack];

        const secondaryIns = updateDataSecondaryInsuranceNewDesign(merged) as Insurance;
        secondaryIns.is_secondary = true;
        secondaryIns.front_of_card = mapMediaToInsuranceMedia(sFront, 'front_of_card');
        secondaryIns.back_of_card = mapMediaToInsuranceMedia(sBack, 'back_of_card');
        payload.push(secondaryIns);
      }

      await createPatientInsurance({ user_id: user.id, insurance: payload }, userRole as UserRoleName);
      toast.success('Insurance information saved.', { className: 'fs-unmask' });
      // Move the dirty-detection baseline forward immediately: callers aren't required to pass
      // `onUserSaved` (e.g. UserProfilePage.tsx doesn't), so nothing else here refreshes `user`
      // to re-trigger the `[user]`-keyed hydration effect that would otherwise do this.
      savedTiersRef.current = { primary, secondary };
      setIsDirty?.(false);
      await onUserSaved?.();
    } catch (e) {
      displayErrorDetails(e);
    } finally {
      setSaving(false);
    }
  }, [user, primary, secondary, onUserSaved, setIsDirty]);

  const handleRemovePrimary = useCallback(async () => {
    if (!user?.id) return;
    setSaving(true);
    try {
      await removePatientInsurance(user.id, userRole as UserRoleName);
      setPrimary(emptyTier());
      setRemoveInsuranceModal(false);
      toast.success('Primary insurance removed.', { className: 'fs-unmask' });
      await onUserSaved?.();
    } catch (e) {
      displayErrorDetails(e);
    } finally {
      setSaving(false);
    }
  }, [user, onUserSaved]);

  const renderCardUploads = (
    tier: InsuranceTierFormState,
    update: (p: Partial<InsuranceTierFormState>) => void,
    side: 'primary' | 'secondary',
  ) => {
    const frontInputId = `${side}-front-card`;
    const backInputId = `${side}-back-card`;

    const removeFrontFile = (fileToRemove: UploadedFile): void => {
      update({
        frontFiles: tier.frontFiles.filter((f) => !(f.name === fileToRemove.name && f.dataURL === fileToRemove.dataURL)),
      });
    };

    const removeBackFile = (fileToRemove: UploadedFile): void => {
      update({
        backFiles: tier.backFiles.filter((f) => !(f.name === fileToRemove.name && f.dataURL === fileToRemove.dataURL)),
      });
    };

    const removeRetainedFrontFile = (fileToRemove: RetainedUploadedFile): void => {
      update({
        retainedFrontFiles: tier.retainedFrontFiles.filter((f) => f.mediaId !== fileToRemove.mediaId),
        retainedFrontMediaIds: tier.retainedFrontMediaIds.filter((id) => id !== fileToRemove.mediaId),
      });
    };

    const removeRetainedBackFile = (fileToRemove: RetainedUploadedFile): void => {
      update({
        retainedBackFiles: tier.retainedBackFiles.filter((f) => f.mediaId !== fileToRemove.mediaId),
        retainedBackMediaIds: tier.retainedBackMediaIds.filter((id) => id !== fileToRemove.mediaId),
      });
    };

    return (
      <div>
        <Typography component="p" className={styles.insuranceCardUploadLabel}>
          Upload your insurance card (optional)
        </Typography>
        <div className={styles.insuranceCardUploadRow}>
          <label htmlFor={frontInputId} className={styles.insuranceCardDropZone}>
            <input
              id={frontInputId}
              type="file"
              accept="image/*,.pdf"
              style={{ display: 'none' }}
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (!f) return;
                const up = await readFileAsUploaded(f);
                update({
                  frontFiles: [...tier.frontFiles, up],
                });
              }}
            />
            <Typography component="p" className={styles.insuranceCardDropTitle}>
              <span>Select files to upload</span> or drag and drop
            </Typography>
            <span className={styles.insuranceCardDropBrowse}>
              <IoDocumentOutline className={styles.insuranceCardDropIcon} />
              Add front of card
            </span>
            {(tier.retainedFrontFiles.length > 0 || tier.frontFiles.length > 0) && (
              <div className={styles.insuranceFileGrid}>
                {tier.retainedFrontFiles.map((file) => (
                  <div key={`retained-${file.name}-${file.dataURL}`} className={styles.insuranceFileCard}>
                    <button
                      type="button"
                      className={styles.insuranceFileRemove}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeRetainedFrontFile(file);
                      }}
                      aria-label={`Remove ${file.name}`}
                    >
                      <IoClose />
                    </button>
                    {isImageFileDataUrl(file.dataURL, file.name) ? (
                      <img src={file.dataURL} alt={file.name} className={styles.insuranceFilePreviewImage} />
                    ) : (
                      <div className={styles.insuranceFilePreviewDoc}>{truncateFilename(file.name, 22)}</div>
                    )}
                  </div>
                ))}
                {tier.frontFiles.map((file) => (
                  <div key={`${file.name}-${file.dataURL}`} className={styles.insuranceFileCard}>
                    <button
                      type="button"
                      className={styles.insuranceFileRemove}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeFrontFile(file);
                      }}
                      aria-label={`Remove ${file.name}`}
                    >
                      <IoClose />
                    </button>
                    {isImageFileDataUrl(file.dataURL, file.name) ? (
                      <img src={file.dataURL} alt={file.name} className={styles.insuranceFilePreviewImage} />
                    ) : (
                      <div className={styles.insuranceFilePreviewDoc}>{truncateFilename(file.name, 22)}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </label>
          <label htmlFor={backInputId} className={styles.insuranceCardDropZone}>
            <input
              id={backInputId}
              type="file"
              accept="image/*,.pdf"
              style={{ display: 'none' }}
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (!f) return;
                const up = await readFileAsUploaded(f);
                update({
                  backFiles: [...tier.backFiles, up],
                });
              }}
            />
            <Typography component="p" className={styles.insuranceCardDropTitle}>
              <span>Select files to upload</span> or drag and drop
            </Typography>
            <span className={styles.insuranceCardDropBrowse}>
              <IoDocumentOutline className={styles.insuranceCardDropIcon} />
              Add back of card
            </span>
            {(tier.retainedBackFiles.length > 0 || tier.backFiles.length > 0) && (
              <div className={styles.insuranceFileGrid}>
                {tier.retainedBackFiles.map((file) => (
                  <div key={`retained-${file.name}-${file.dataURL}`} className={styles.insuranceFileCard}>
                    <button
                      type="button"
                      className={styles.insuranceFileRemove}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeRetainedBackFile(file);
                      }}
                      aria-label={`Remove ${file.name}`}
                    >
                      <IoClose />
                    </button>
                    {isImageFileDataUrl(file.dataURL, file.name) ? (
                      <img src={file.dataURL} alt={file.name} className={styles.insuranceFilePreviewImage} />
                    ) : (
                      <div className={styles.insuranceFilePreviewDoc}>{truncateFilename(file.name, 22)}</div>
                    )}
                  </div>
                ))}
                {tier.backFiles.map((file) => (
                  <div key={`${file.name}-${file.dataURL}`} className={styles.insuranceFileCard}>
                    <button
                      type="button"
                      className={styles.insuranceFileRemove}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeBackFile(file);
                      }}
                      aria-label={`Remove ${file.name}`}
                    >
                      <IoClose />
                    </button>
                    {isImageFileDataUrl(file.dataURL, file.name) ? (
                      <img src={file.dataURL} alt={file.name} className={styles.insuranceFilePreviewImage} />
                    ) : (
                      <div className={styles.insuranceFilePreviewDoc}>{truncateFilename(file.name, 22)}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </label>
        </div>
      </div>
    );
  };

  const renderTierFields = (
    tier: InsuranceTierFormState,
    update: (p: Partial<InsuranceTierFormState>) => void,
    side: 'primary' | 'secondary',
    showStateFunded: boolean,
  ) => (
    <>
      <MUISelectInput
        name={`${side}_carrier`}
        label="Insurance company"
        required
        value={tier.carrierId === '' ? '' : String(tier.carrierId)}
        onChange={(v: string) => update({ carrierId: v ? Number(v) : '' })}
        options={carrierOptions}
      />

      <div className={styles.fieldRow}>
        <TextField
          name={`${side}_member`}
          label="Member ID"
          value={tier.memberId}
          onChange={(e) => update({ memberId: e.target.value })}
        />
        <TextField
          name={`${side}_group`}
          label="Group number (optional)"
          value={tier.groupNumber}
          onChange={(e) => update({ groupNumber: e.target.value })}
        />
      </div>

      <PhoneInput
        name={`${side}_phone`}
        label="Insurance phone number"
        value={tier.phone as E164Number}
        onChange={(value?: string) => update({ phone: value || '' })}
        required
        errors={{
          [`${side}_phone`]: {
            message: side === 'primary' ? phoneErrors.primary : phoneErrors.secondary,
          },
        }}
      />

      <TextField
        name={`${side}_addr`}
        label="Insurance street address"
        value={tier.address1}
        onChange={(e) => update({ address1: e.target.value })}
      />

      <div className={styles.fieldRow}>
        <TextField
          name={`${side}_city`}
          label="Insurance city"
          value={tier.city}
          onChange={(e) => update({ city: e.target.value })}
        />
        <MUISelectInput
          name={`${side}_state`}
          label="Insurance state"
          required
          value={tier.state}
          onChange={(v: string) => update({ state: v })}
          options={stateSelectOptions}
        />
        <TextField
          name={`${side}_zip`}
          label="Insurance zip code"
          value={tier.zip}
          onChange={(e) => update({ zip: e.target.value })}
          errors={{
            [`${side}_zip`]: {
              message: side === 'primary' ? zipErrors.primaryZip : zipErrors.secondaryZip,
            },
          }}
        />
      </div>

      {renderCardUploads(tier, update, side)}

      <Typography component="p" className={styles.sectionDescription}>
        Are you the policy holder for this insurance? (optional)
      </Typography>
      <div className={styles.segmentedToggle}>
        <button
          type="button"
          className={`${styles.segmentedToggleBtn} ${tier.insuredByRelation === 'myself' ? styles.segmentedToggleBtnActive : ''}`}
          onClick={() => update({ insuredByRelation: 'myself' })}
        >
          Yes
        </button>
        <button
          type="button"
          className={`${styles.segmentedToggleBtn} ${tier.insuredByRelation !== 'myself' ? styles.segmentedToggleBtnActive : ''}`}
          onClick={() => update({ insuredByRelation: tier.insuredByRelation === 'myself' ? 'spouse' : tier.insuredByRelation })}
        >
          No
        </button>
      </div>

      {tier.insuredByRelation !== 'myself' && (
        <>
          <MUISelectInput
            name={`${side}_insured_by_relation`}
            label="Who is the policy holder?"
            required={false}
            value={tier.insuredByRelation}
            onChange={(v: string) => update({ insuredByRelation: v || 'spouse' })}
            options={relationshipOptions}
          />
          <Typography component="h2" className={styles.sectionTitle}>
            Policy holder information
          </Typography>
          <div className={styles.fieldRow}>
            <TextField
              name={`${side}_sub_fn`}
              label="First name"
              value={tier.subFirstName}
              onChange={(e) => update({ subFirstName: e.target.value })}
            />
            <TextField
              name={`${side}_sub_ln`}
              label="Last name"
              value={tier.subLastName}
              onChange={(e) => update({ subLastName: e.target.value })}
            />
          </div>
          <div className={styles.fieldRow}>
            <TextField
              name={`${side}_sub_dob`}
              label="Date of birth"
              type="date"
              value={tier.subDob}
              onChange={(e) => update({ subDob: e.target.value })}
            />
            <MUISelectInput
              name={`${side}_sub_gender`}
              label="Gender assigned at birth"
              required
              value={tier.subGender}
              onChange={(v: string) => update({ subGender: v })}
              options={genderSelectOptions}
            />
          </div>

          <label className={styles.subscriberSameAddressRow}>
            <Checkbox
              checked={tier.subscriberSameAddress}
              onChange={(_, c) => update({ subscriberSameAddress: c })}
              size="small"
            />
            <span>Subscriber has same address</span>
          </label>

          {!tier.subscriberSameAddress && (
            <>
              <div className={styles.fieldRow}>
                <TextField
                  name={`${side}_sub_a1`}
                  label="Address"
                  value={tier.subAddress1}
                  onChange={(e) => update({ subAddress1: e.target.value })}
                />
                <TextField
                  name={`${side}_sub_a2`}
                  label="Apartment, suite, etc. (optional)"
                  value={tier.subAddress2}
                  onChange={(e) => update({ subAddress2: e.target.value })}
                />
              </div>
              <div className={styles.fieldRow}>
                <TextField
                  name={`${side}_sub_city`}
                  label="City"
                  value={tier.subCity}
                  onChange={(e) => update({ subCity: e.target.value })}
                />
                <MUISelectInput
                  name={`${side}_sub_state`}
                  label="State"
                  required
                  value={tier.subState}
                  onChange={(v: string) => update({ subState: v })}
                  options={stateSelectOptions}
                />
                <TextField
                  name={`${side}_sub_zip`}
                  label="Zip code"
                  value={tier.subZip}
                  onChange={(e) => update({ subZip: e.target.value })}
                  errors={{
                    [`${side}_sub_zip`]: {
                      message: side === 'primary' ? zipErrors.primarySubZip : zipErrors.secondarySubZip,
                    },
                  }}
                />
              </div>
            </>
          )}
        </>
      )}

      {showStateFunded && (
        <div>
          <Typography className={styles.sectionDescription} component="p">
            Do you currently have insurance coverage through a state-funded program such as Medicaid? *
          </Typography>
          <div className={styles.segmentedToggle}>
            {STATE_FUNDED_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`${styles.segmentedToggleBtn} ${tier.stateFunded === opt.value ? styles.segmentedToggleBtnActive : ''}`}
                onClick={() => update({ stateFunded: opt.value })}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );

  if (!user) {
    return (
      <div className={styles.form}>
        <Typography className={styles.emptyState}>No user loaded.</Typography>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void handleSave();
      }}
      onKeyDown={(e) => {
        // Only a real `<form>` since the unsaved-changes modal needs to submit it; guard against
        // the native browser behavior of implicitly submitting on Enter in a text field, which
        // didn't exist when this was a plain `<div>`.
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault();
        }
      }}
    >
      <InsuranceTierPanel
        title="Primary Insurance information"
        headerCarrierName={primaryCarrierName}
        expanded={primaryExpanded}
        onToggle={() => setPrimaryExpanded((e) => !e)}
      >
        {renderTierFields(primary, updatePrimary, 'primary', true)}

        <div className={styles.insuranceSectionFooter}>
          <button type="button" className={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <button type="button" className={styles.removeLink} onClick={() => setRemoveInsuranceModal(true)} disabled={saving}>
            Remove primary insurance
          </button>
        </div>
      </InsuranceTierPanel>

      <InsuranceTierPanel
        title="Secondary Insurance information"
        headerCarrierName={secondaryCarrierName}
        expanded={secondaryExpanded}
        onToggle={() => setSecondaryExpanded((e) => !e)}
      >
        {renderTierFields(secondary, updateSecondary, 'secondary', false)}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Use &quot;Save changes&quot; on the primary section to save secondary insurance together with primary.
        </Typography>
      </InsuranceTierPanel>
      {removeInsuranceModal && (
        <RemoveItemModal
          isOpen={removeInsuranceModal}
          toggleModal={setRemoveInsuranceModal}
          itemName="this insurance"
          removeItem={handleRemovePrimary}
        />
      )}
    </form>
  );
};

export default CollectorInsuranceTab;
